import { graphql } from 'react-relay';

import { isSSR } from '@atlaskit/editor-common/is-ssr';
import EmojiResource from '@atlaskit/emoji/emoji-resource';
import { prefetchNodeDataProvidersData } from '@atlaskit/node-data-provider/prefetch-node-data-providers-data';
import CardClient from '@atlaskit/link-provider/client';
import { isJSONDocNode } from '@atlaskit/editor-json-transformer';
import { isInternalUrl, isExternalUrl } from '@atlaskit/editor-card-provider/provider';

import { createEmojiProviderParameters } from '@confluence/fabric-emoji-support';
import { getEmojiNodeDataProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { createCookie } from '@confluence/content-smartlinks/entry-points/createCookie.ts';
import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';
import type { SupportedUrlFilter } from '@confluence/smartlinks';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import type {
	preloadEditorNodeDataProvidersDataQuery$data,
	preloadEditorNodeDataProvidersDataQuery$variables,
} from './__generated__/preloadEditorNodeDataProvidersDataQuery.graphql.ts';
import {
	NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH,
	NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
} from './utils/nodeDataProvidersConstants.ts';
import { addToSSREditorNodeDataProvidersStore } from './utils/ssrDataStore.ts';
import { preloadEditorSyncedBlocksData } from './preloadEditorSyncedBlocksData.ts';
import { isValidJSONDocNode } from './utils/isValidJSONDocNode.ts';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/editor/nodeDataProvidersData';
const DEFAULT_TIMEOUT = 300;
const EDITOR_CARD_PROVIDER_NAME = 'editorCardProvider' as const;
const EXTERNAL_CARD_PROVIDER_NAME = 'externalEditorCardProvider' as const;

const preloadEditorNodeDataProvidersDataQuery = graphql`
	query preloadEditorNodeDataProvidersDataQuery($contentId: ID, $status: [String]) {
		singleContent(id: $contentId, status: $status) {
			id
			body {
				atlas_doc_format {
					value
				}
			}
		}
	}
`;

export async function preloadEditorNodeDataProvidersData(
	contentId: string | null,
	cloudId?: string,
	isBlog?: boolean,
): Promise<void> {
	if (!contentId) {
		return;
	}

	if (!isSSR()) {
		// If we are not in SSR, we don't need to preload node data providers data.
		return;
	}

	await SSRMeasures.run(MEASURE_SEGMENT, async () => {
		const fetchAdfStart = performance.now();
		const docJsonString = await fetchAdf(contentId);

		const fetchAdfDuration = performance.now() - fetchAdfStart;

		if (!docJsonString) {
			return;
		}

		const doc = JSON.parse(docJsonString);

		// Validate that the parsed document is a valid JSONDocNode
		if (!isValidJSONDocNode(doc)) {
			return;
		}

		const providers: Parameters<typeof prefetchNodeDataProvidersData>[0]['providers'] = [];

		let syncedBlocksPromise: Promise<void> | undefined;
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (expVal('platform_synced_block', 'isEnabled', false)) {
			if (isJSONDocNode(doc)) {
				syncedBlocksPromise = preloadEditorSyncedBlocksData(contentId, cloudId, isBlog, doc);
			}
		}

		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (
			expValEquals('cc_editor_node_data_providers_preload_config', 'isEnabled', true) &&
			expValEquals('cc_editor_node_data_providers_preload_config', 'isSplittingEnabled', true)
		) {
			providers.push({
				provider: ssrEditorInternalCardProvider(),
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.smartCard,
			});

			providers.push({
				provider: ssrEditorExternalCardProvider(),
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.smartCard,
			});
		} else {
			providers.push({
				provider: ssrEditorCardProvider(),
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.smartCard,
			});
		}

		const emojiNodeDataProvider = await ssrEmojiNodeDataProvider();
		if (emojiNodeDataProvider) {
			providers.push({
				provider: emojiNodeDataProvider,
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.emoji,
			});
		}

		providers.forEach(({ provider }) => {
			SSRMeasures.markMeasureStart(measureProviderSegment(provider.name));
		});

		let editorNodeDataProvidersData = await prefetchNodeDataProvidersData({
			doc,
			providers,
			timeout:
				(expValEquals('cc_editor_node_data_providers_preload_config', 'isEnabled', true)
					? // eslint-disable-next-line confluence-feature-gating/inline-expVal-params
						expVal(
							'cc_editor_node_data_providers_preload_config',
							'defaultTimeout',
							DEFAULT_TIMEOUT,
						) || DEFAULT_TIMEOUT
					: DEFAULT_TIMEOUT) - fetchAdfDuration,
			maxNodesToVisit: NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
		});

		Object.entries(editorNodeDataProvidersData).forEach(([providerName, { duration }]) => {
			SSRMeasures.setMeasureDuration(measureProviderSegment(providerName), duration);
		});

		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (
			expValEquals('cc_editor_node_data_providers_preload_config', 'isEnabled', true) &&
			expValEquals('cc_editor_node_data_providers_preload_config', 'isSplittingEnabled', true)
		) {
			editorNodeDataProvidersData = mergeCardProviderResults(editorNodeDataProvidersData);
		}

		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('platform_synced_block', 'isEnabled', false)
		) {
			const nodeDataToStore = Object.fromEntries(
				Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
					providerName,
					data,
				]),
			);
			addToSSREditorNodeDataProvidersStore(nodeDataToStore);
		} else {
			window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] = Object.fromEntries(
				Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
					providerName,
					data,
				]),
			);
		}

		// Ensure synced blocks data is fully resolved before returning
		if (syncedBlocksPromise) {
			await syncedBlocksPromise;
		}
	});
}

export type NodeDataEntry = { data: Record<string, unknown>; duration: number; success: boolean };
export type NodeDataProvidersSsrData = Record<string, NodeDataEntry>;

export function mergeCardProviderResults(
	nodeDataProvidersSsrData: NodeDataProvidersSsrData,
	// will merge to the first provider in the list, which is `editorCardProvider` in our case, so it should be listed first in the array
	keysOfProviderDataToMerge: string[] = [EDITOR_CARD_PROVIDER_NAME, EXTERNAL_CARD_PROVIDER_NAME],
): NodeDataProvidersSsrData {
	if (keysOfProviderDataToMerge.length < 2) {
		return nodeDataProvidersSsrData;
	}

	const [mainProviderName, ...sourceKeys] = keysOfProviderDataToMerge;
	const result: NodeDataProvidersSsrData = { ...nodeDataProvidersSsrData };
	const processedSourceKeys = new Set<string>();

	for (const sourceKey of sourceKeys) {
		if (sourceKey === mainProviderName || processedSourceKeys.has(sourceKey)) {
			continue;
		}

		processedSourceKeys.add(sourceKey);
		const sourceData = result[sourceKey];

		if (sourceData) {
			result[mainProviderName] = result[mainProviderName] || {
				data: {},
				duration: 0,
				success: false,
			};

			result[mainProviderName] = {
				data: {
					...result[mainProviderName].data,
					...sourceData.data,
				},
				duration: Math.max(result[mainProviderName].duration, sourceData.duration),
				success: result[mainProviderName].success || sourceData.success,
			};

			delete result[sourceKey];
		}
	}
	return result;
}

function fetchAdf(contentId: string | undefined | null): Promise<string | undefined> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/fetch:AdfDocQuery`, async () => {
		try {
			const result = await query<
				preloadEditorNodeDataProvidersDataQuery$data,
				preloadEditorNodeDataProvidersDataQuery$variables
			>({
				query: preloadEditorNodeDataProvidersDataQuery,
				variables: {
					contentId,
					status: ['draft'],
				},
				fetchPolicy: 'network-only',
			});

			if (result.hasErrors) {
				return undefined;
			}

			return result.data?.singleContent?.body?.atlas_doc_format?.value || undefined;
		} catch {
			return undefined;
		}
	});
}

async function ssrEmojiNodeDataProvider() {
	const { userId, cloudId, isLicensed, isLoggedIn } = getPreloaderFnContext();

	// We need a special emoji parameters that supports SSR load optimization here.
	const emojiResourceParams = createEmojiProviderParameters({
		cloudId,
		userId,
		isLicensed,
		isLoggedIn,
	});

	return getEmojiNodeDataProvider(EmojiResource, {
		...emojiResourceParams,
		allowAnonymousAccess: true,
	});
}

function ssrEditorCardProvider() {
	const { host, cloudId, userContext, customURLs } = getSSRRenderInputs();

	const objectResolverURL =
		customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

	const ssrCardClient = new CardClient('custom', objectResolverURL);
	ssrCardClient.setHeaders({
		Cookie: createCookie(userContext),
		Origin: host.startsWith('http') ? host : `https://${host}`,
		CloudId: cloudId,
	});

	// No URL filter: accepts both internal and external URLs
	return new ConfluenceEditorCardProvider('custom', objectResolverURL, undefined, ssrCardClient);
}

function createSSREditorCardProvider(urlFilter: SupportedUrlFilter, nameOverride?: string) {
	const { host, cloudId, userContext, customURLs } = getSSRRenderInputs();

	// This URL will be something like `https://object-resolver.staging.atl-paas.net/resolve/batch`.
	// We need only protocol and host to pass it to the EditorCardProvider,
	// because path will be added by `CardClient`.
	const objectResolverURL =
		customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

	// We need to define a custom CardClient for SSR, because the default one works through public API gateway.
	const ssrCardClient = new CardClient('custom', objectResolverURL);
	// Headers from `fetchORSForSSR` from confluence/next/packages/content-smartlinks/src/index.ts
	ssrCardClient.setHeaders({
		Cookie: createCookie(userContext),
		Origin: host.startsWith('http') ? host : `https://${host}`,
		CloudId: cloudId,
	});

	return new ConfluenceEditorCardProvider(
		'custom',
		objectResolverURL,
		undefined,
		ssrCardClient,
		urlFilter,
		nameOverride,
	);
}

export function ssrEditorInternalCardProvider(): ConfluenceEditorCardProvider {
	return createSSREditorCardProvider(isInternalUrl);
}

export function ssrEditorExternalCardProvider(): ConfluenceEditorCardProvider {
	return createSSREditorCardProvider(isExternalUrl, EXTERNAL_CARD_PROVIDER_NAME);
}

function measureProviderSegment(providerName: string) {
	return `${MEASURE_SEGMENT}/${providerName}`;
}
